(function () {
  'use strict';

  let isInitialized = false;
  let isProcessingRequest = false;
  let processedRequests = new Set();
  let allSizeVariants = [];
  let sizeVariantStocks = {};
  let sizeDistribution = {};
  let variantDistributions = {};
  let isBedenSetiFormat = false;
  let minStockQuantity = 0;
  let availableSizes = [];
  let internalRequestFlag = 'SET_MULTIPLIER_INTERNAL_REQUEST';
  let isPaketYapisiFormat = false;
  let paketMultiplier = 1;
  const PRODUCTION_MODE = false;

  // Sepette zaten bulunan miktar (variant_id → toplam adet) — stok kontrolünde stoktan düşülür
  // Bu cache olmadan: stok=8, 1.set add geçer, 2.set add yine geçer (cache olmadığı için stok 8 sanıyor)
  let cartConsumedByVariant = new Map();
  let cartConsumedFetchedAt = 0;
  const CART_CONSUMED_TTL = 1500; // ms

  async function refreshCartConsumedCache(force = false) {
    if (!force && cartConsumedFetchedAt > 0 && (Date.now() - cartConsumedFetchedAt) < CART_CONSUMED_TTL) {
      return cartConsumedByVariant;
    }
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return cartConsumedByVariant;
      const cart = await response.json();
      const map = new Map();
      for (const item of (cart.items || [])) {
        const vid = String(item.variant_id);
        map.set(vid, (map.get(vid) || 0) + (item.quantity || 0));
      }
      cartConsumedByVariant = map;
      cartConsumedFetchedAt = Date.now();
    } catch (e) {
      // sessizce geç — cache eski haliyle kalır
    }
    return cartConsumedByVariant;
  }

  function getCartConsumed(variantId) {
    if (variantId == null) return 0;
    return cartConsumedByVariant.get(String(variantId)) || 0;
  }

  // ============ LOT ALGORİTMASI ============
  // İdeal dağılım önceliklidir. Stok ideal'e tam bölünmezse:
  //   Katman 1: ideal setler → Katman 2: T parçalık dengeli karma lotlar
  // (Katman 3 — kısmi lot — KAPALI. T'den az kalan stok satılmaz.)

  // Round-robin: stoktan T parça dengeli al
  function balancedTake(stockObj, T) {
    const result = {};
    const sizes = Object.keys(stockObj);
    for (const s of sizes) result[s] = 0;
    let taken = 0, guard = 0;
    while (taken < T && guard < 100000) {
      guard++;
      let progressed = false;
      for (const s of sizes) {
        if (taken >= T) break;
        if ((stockObj[s] || 0) - result[s] > 0) {
          result[s]++; taken++; progressed = true;
        }
      }
      if (!progressed) break;
    }
    return result;
  }

  // ideal: {size: qty}, stock: {size: qty} → sıralı lot listesi [{type, dist}]
  function computeLots(ideal, stock) {
    const idealSizes = Object.keys(ideal || {});
    const T = idealSizes.reduce((a, s) => a + (ideal[s] || 0), 0);
    const lots = [];
    if (T <= 0) return lots;

    const kalan = {};
    for (const s of Object.keys(stock || {})) kalan[s] = Math.max(0, stock[s] || 0);
    for (const s of idealSizes) if (!(s in kalan)) kalan[s] = 0;

    // Katman 1 — ideal setler
    let idealAdet = Infinity;
    for (const s of idealSizes) {
      if ((ideal[s] || 0) <= 0) continue;
      const possible = Math.floor((kalan[s] || 0) / ideal[s]);
      if (possible < idealAdet) idealAdet = possible;
    }
    if (!isFinite(idealAdet) || idealAdet < 0) idealAdet = 0;
    for (let i = 0; i < idealAdet; i++) {
      const dist = {};
      for (const s of idealSizes) { dist[s] = ideal[s]; kalan[s] -= ideal[s]; }
      lots.push({ type: 'ideal', dist });
    }

    const sumKalan = () => Object.keys(kalan).reduce((a, s) => a + Math.max(0, kalan[s]), 0);

    // Katman 2 — karma lotlar
    let guard = 0;
    while (sumKalan() >= T && guard < 5000) {
      guard++;
      const lot = balancedTake(kalan, T);
      const lotSum = Object.keys(lot).reduce((a, s) => a + lot[s], 0);
      if (lotSum < T) break;
      const dist = {};
      for (const s of Object.keys(lot)) {
        kalan[s] -= lot[s];
        if (lot[s] > 0) dist[s] = lot[s];
      }
      lots.push({ type: 'karma', dist });
    }

    // Katman 3 KAPALI — Kısmi lot eklenmez. T parçalık tam/karma lotlardan
    // küçük kalan stok satılmaz (tükendi sayılır). Müşteri istegi:
    // "set toplamı 6'ysa en fazla N tam set, kalan stok eklenmesin".
    return lots;
  }

  // İlk n lotun dağılımları toplamı → {size: qty}
  function mergeLotsUnion(lots, n) {
    const union = {};
    const limit = Math.max(0, Math.min(n, lots.length));
    for (let i = 0; i < limit; i++) {
      for (const [s, q] of Object.entries(lots[i].dist || {})) {
        union[s] = (union[s] || 0) + q;
      }
    }
    return union;
  }

  // distribution anahtarlarını normalize et (uppercase)
  function normalizeDistribution(dist) {
    const out = {};
    for (const k of Object.keys(dist || {})) {
      out[normalizeSize(k)] = dist[k];
    }
    return out;
  }

  // sizeVariantStocks'tan {normalizedSize: kalanStok} — sepetteki miktar düşülmüş
  // RENK FİLTRESİ: seçili renge ait varyantlar — diğer renkler havuza karışmaz
  function buildProductStockBySize() {
    const stockBySize = {};
    const selColor = normalizeColor(getSelectedColor());
    for (const variantId in sizeVariantStocks) {
      const info = sizeVariantStocks[variantId];
      if (!info) continue;
      if (selColor && normalizeColor(info.colorName) !== selColor) continue;
      const sizeName = normalizeSize(info.sizeName);
      const rawStock = typeof info.stock === 'number' ? info.stock : 0;
      const consumed = getCartConsumed(variantId);
      const avail = Math.max(0, rawStock - consumed);
      stockBySize[sizeName] = (stockBySize[sizeName] || 0) + avail;
    }
    return stockBySize;
  }

  // Debounce helper - tekrarlayan çağrıları engelle
  let debounceTimers = {};
  function debounce(key, callback, delay = 300) {
    clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(callback, delay);
  }

  function log(...args) {
    if (!PRODUCTION_MODE) {
      console.log('[Set Multiplier]', ...args);
    }
  }

  function logError(...args) {
    console.error('[Set Multiplier Error]', ...args);
  }

  function isEN() {
    try {
      if (window.Shopify && window.Shopify.locale && String(window.Shopify.locale).toLowerCase().startsWith('en')) return true;
    } catch (e) {}
    const path = (location && location.pathname) || '';
    return /^\/en(\/|$)/i.test(path);
  }

  const I18N = {
    setProductLabel:    { tr: 'Set Ürün:',                              en: 'Set Product:' },
    packageProductLabel:{ tr: 'Paket Ürün:',                            en: 'Package Product:' },
    soldAsSet:          { tr: 'Bu ürün set halinde satılmaktadır.',     en: 'This product is sold as a set.' },
    soldAsPackage:      { tr: 'Bu ürün paket halinde satılmaktadır.',   en: 'This product is sold as a package.' },
    stdPerOrder:        { tr: (n) => `Her 1 adet sipariş için ${n} adet ürün sepetinize eklenecektir.`,
                          en: (n) => `${n} items will be added to your cart per order.` },
    sizeSetPerOne:      { tr: (n) => `Her 1 adet için ${n} farklı beden ürünü sepetinize eklenecektir.`,
                          en: (n) => `${n} items in different sizes will be added to your cart per unit.` },
    distribution:       { tr: 'Dağılım:',                               en: 'Distribution:' },
    pcsSuffix:          { tr: (n) => `${n} adet`,                       en: (n) => `${n} pcs` },
    maxAddable:         { tr: (n) => `Maksimum Eklenebilir: ${n} lot`,  en: (n) => `Maximum addable: ${n} lots` },
    mixedNote:          { tr: 'İdeal set kurulamazsa kalan stok karma lot olarak eklenir.',
                          en: 'If ideal set cannot be formed, remaining stock is added as mixed lots.' },
    yourSelection:      { tr: 'Seçiminiz:',                             en: 'Your selection:' },
    addingToCart:       { tr: 'Sepete eklenecek:',                      en: 'Adding to cart:' },
    moreAddable:        { tr: (sets, items, unit) => `Bu sepete daha eklenebilir: ${sets} ${unit} (${items} adet)`,
                          en: (sets, items, unit) => `Can still add to cart: ${sets} ${unit} (${items} pcs)` },
    setUnit:            { tr: 'set',                                    en: 'sets' },
    lotUnit:            { tr: 'lot',                                    en: 'lots' },
    packageUnit:        { tr: 'paket',                                  en: 'packages' },
    lotEquals:          { tr: (n) => `${n} adet`,                       en: (n) => `${n} pcs` },
    lotContents:        { tr: (t) => `Her lot içeriği: ${t}`,           en: (t) => `Each lot contains: ${t}` },
    maxAddableShort:    { tr: (n) => `Maksimum eklenebilir: ${n} lot`,  en: (n) => `Maximum addable: ${n} lots` },
    addedWillBe:        { tr: (q, m, t) => `${q} × ${m} = ${t} adet`,   en: (q, m, t) => `${q} × ${m} = ${t} pcs` },
    pkgAdding:          { tr: (q, m, t) => `${q} paket × ${m} = ${t} adet`, en: (q, m, t) => `${q} × ${m} = ${t} pcs` },
    stdAdding:          { tr: (q, t, f) => `${q} adet × ${t} = ${f} adet ürün`, en: (q, t, f) => `${q} × ${t} = ${f} pcs` },
    lotAdding:          { tr: (q, t) => `${q} lot = ${t} adet`,         en: (q, t) => `${q} lots = ${t} pcs` },
    setPriceLabel:      { tr: (n) => `Set Fiyatı (${n} ürün):`,         en: (n) => `Set Price (${n} items):` },
    addedToastTitle:    { tr: 'Sepete eklendi',                         en: 'Added to cart' }
  };

  function T(key, ...args) {
    const entry = I18N[key];
    if (!entry) return '';
    const val = isEN() ? (entry.en || entry.tr) : entry.tr;
    return typeof val === 'function' ? val(...args) : val;
  }

  function initializeMultiplier() {
    if (!window.productVariantInfo) {
      logError('Product variant info not found');
      return false;
    }

    const info = window.productVariantInfo;
    allSizeVariants = info.allSizeVariantIds || [];
    minStockQuantity = info.minStockQuantity || 0;
    availableSizes = info.allSizes || [];

    log('Product info loaded:', info.productTitle);

    if (allSizeVariants.length === 0) {
      logError('No size variants found');
      return false;
    }

    parseVariantStocks(info.sizeVariantStocks);

    if (info.isBedenSetiFormat && info.setDistribution &&
      (info.setDistribution.includes('|') || info.setDistribution.includes(','))) {
      isBedenSetiFormat = true;
      variantDistributions = parseVariantDistributions(info.setDistribution);
      log('Beden Seti format detected');
    } else if (info.setDistribution && (info.setDistribution.includes('Paket Yapısı:') || info.setDistribution.includes('Package Structure:') || info.setDistribution.includes('Package:'))) {
      isPaketYapisiFormat = true;
      paketMultiplier = parsePaketYapisi(info.setDistribution);
      log('Paket Yapısı format detected, multiplier:', paketMultiplier);
    } else {
      isBedenSetiFormat = false;
      isPaketYapisiFormat = false;
      sizeDistribution = parseSetDistribution(info.setDistribution || 'AUTO');
      log('Traditional format detected');
    }

    if (isBedenSetiFormat) {
      if (Object.keys(variantDistributions).length === 0) {
        logError('Could not parse variant distributions');
        return false;
      }
    } else {
      if (Object.keys(sizeDistribution).length === 0) {
        logError('Could not parse size distribution');
        return false;
      }
    }

    log('Set Multiplier initialized successfully');

    moveSetInfoToCorrectPosition();
    updateSetInfoDisplay();
    injectSetPriceDisplay();

    return true;
  }

  // PDP'de birim fiyatın altına SET FİYATI bilgisini inject et
  function injectSetPriceDisplay() {
    try {
      if (!sizeDistribution || Object.keys(sizeDistribution).length === 0) return;

      // Lot = Set İçerik Bilgisi toplamı (örn 6 ürün) — her lot sabit boyutta
      let totalPerSet = 0;
      const isSTD = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';
      if (isSTD) {
        totalPerSet = sizeDistribution['STD'] || 1;
      } else {
        totalPerSet = Object.values(sizeDistribution).reduce((sum, q) => sum + (Number(q) || 0), 0);
      }

      if (totalPerSet <= 1) return; // 1'lik set için gösterme — ek bilgi yok

      // Tema'nın birim fiyat element'ini bul
      const priceEl = document.querySelector('.product-price__current, [data-product-price] .product-price__current, .product-price [data-price]');
      if (!priceEl) {
        log('injectSetPriceDisplay: birim fiyat element bulunamadı');
        return;
      }

      const priceContainer = priceEl.parentNode;
      // Çift inject önleme
      if (priceContainer.querySelector('.product-price__set-total')) return;

      // Birim fiyat: manuel kur çevirici (.money span'ına data-tkur-try = TRY kuruş yazar)
      // varsa oradan; yoksa metinden parse et — "1.234,56 TRY" / "1,234.56 TRY" desteklenir.
      let unitPrice = NaN;
      const moneyEl = priceEl.classList.contains('money') ? priceEl : priceEl.querySelector('.money');
      if (moneyEl && moneyEl.dataset && moneyEl.dataset.tkurTry) {
        unitPrice = parseInt(moneyEl.dataset.tkurTry, 10) / 100;
      } else {
        const priceText = (moneyEl || priceEl).textContent.trim();
        const numMatch = priceText.match(/([0-9][0-9.,]*[0-9]|[0-9])/);
        if (!numMatch) return;
        let numericStr = numMatch[0];
        const lastDot = numericStr.lastIndexOf('.');
        const lastComma = numericStr.lastIndexOf(',');
        if (lastComma > lastDot) {
          numericStr = numericStr.replace(/\./g, '').replace(',', '.');
        } else if (lastDot > lastComma) {
          numericStr = numericStr.replace(/,/g, '');
        }
        unitPrice = parseFloat(numericStr);
      }
      if (isNaN(unitPrice) || unitPrice <= 0) return;

      // Set toplamı mağaza formatında (1,234.56TL) ve .money sınıfıyla basılır;
      // manuel kur çevirici seçili para birimine çevirir.
      const setTotalPrice = unitPrice * totalPerSet;
      const setTotalFormatted = setTotalPrice.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + 'TL';

      const setPriceEl = document.createElement('div');
      setPriceEl.className = 'product-price__set-total mt-2 flex items-baseline gap-2';
      setPriceEl.style.flexWrap = 'nowrap';
      setPriceEl.innerHTML = `
        <span style="font-weight:600; text-transform:uppercase; letter-spacing:0.5px; font-size:11px; opacity:0.65; white-space:nowrap;">${T('setPriceLabel', totalPerSet)}</span>
        <span class="money" style="font-weight:700; font-size:16px; white-space:nowrap;">${setTotalFormatted}</span>
      `;
      priceContainer.insertBefore(setPriceEl, priceEl.nextSibling);
      log('Set fiyatı inject edildi:', setTotalFormatted);
    } catch (e) {
      log('injectSetPriceDisplay error:', e);
    }
  }

  function parseVariantStocks(stockString) {
    if (!stockString) return;

    const stockPairs = stockString.split(',');
    stockPairs.forEach(pair => {
      const [variantId, rest] = pair.split(':');
      if (!variantId || !rest) return;
      const [stockStr, sizeName, colorName] = rest.split('|');
      sizeVariantStocks[variantId] = {
        stock: parseInt(stockStr, 10) || 0,
        sizeName: (sizeName || '').trim(),
        colorName: (colorName || '').trim()
      };
    });
  }

  // Optimize edilmiş string temizleme helper fonksiyonu
  function cleanupDistributionText(text) {
    if (!text) return '';
    return text.toString()
      .replace(/<[^>]*>/g, '')
      .replace(/&[a-zA-Z0-9#]+;/g, '')
      .replace(/\*+/g, '')
      .replace(/[\r\n\t\f\v]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseSetDistribution(distributionText) {
    if (!distributionText) return {};

    const distribution = {};
    const cleanText = cleanupDistributionText(distributionText);

    log('Parsing distribution text:', cleanText);

    // STD formatını öncelikli olarak kontrol et
    // Pattern 1: "STD-5" formatı
    let stdMatch = cleanText.match(/STD-(\d+)/i) || cleanText.match(/STD\s*[-:]\s*(\d+)/i);
    if (stdMatch) {
      const multiplier = parseInt(stdMatch[1]);
      log(`STD format detected: STD-${multiplier}`);
      distribution['STD'] = multiplier;
      return distribution;
    }

    // Pattern 2: "5xSTANDART" veya "6xSTANDART" formatı
    stdMatch = cleanText.match(/(\d+)\s*x\s*STANDART/i) ||
      cleanText.match(/(\d+)\s*x\s*STD/i) ||
      cleanText.match(/(\d+)xSTANDART/i) ||
      cleanText.match(/(\d+)xSTD/i);

    if (stdMatch) {
      const multiplier = parseInt(stdMatch[1]);
      log(`xSTANDART format detected: ${multiplier}xSTANDART`);
      distribution['STD'] = multiplier;
      return distribution;
    }

    // Pattern 3: Sadece sayı ile STANDART kelimesi
    stdMatch = cleanText.match(/(\d+)\s+STANDART/i) ||
      cleanText.match(/STANDART\s+(\d+)/i);

    if (stdMatch) {
      const multiplier = parseInt(stdMatch[1]);
      log(`STANDART number format detected: ${multiplier} STANDART`);
      distribution['STD'] = multiplier;
      return distribution;
    }

    // ONE SIZE / TEK BEDEN desteği
    if (/ONE\s*SIZE/i.test(cleanText) || /TEK\s*BEDEN/i.test(cleanText)) {
      log('ONE SIZE format detected');
      distribution['STD'] = 1;
      return distribution;
    }

    // OPTIMIZE EDİLMİŞ: İki pattern'i birleştir
    const excludedWords = new Set(['TRY', 'USD', 'EUR', 'GBP', 'ADET', 'KG', 'TL', 'MAL', 'VE', 'BR', 'DIV', 'EM', 'TEK']);
    const validSizes = new Set(['S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XS', '1XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL']);
    const extendedSizes = new Set(['1XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL']);

    // YENİ: NUMERIC qty+size bitişik - "134-236-238-140" (1 digit qty + 2 digit numeric size)
    // Anlam: "134" = 1 adet 34, "236" = 2 adet 36 (pantolon, etek, şort gibi sayısal bedenli ürünler için)
    // Sadece variant'larda numeric beden olan ürünlerde aktif olur, false-positive önlenir
    if (window.productVariantInfo && window.productVariantInfo.allSizes) {
      const availableSizesStr = window.productVariantInfo.allSizes.map(String);
      const hasNumericSizes = availableSizesStr.some(s => /^\d{2,3}$/.test(s));

      if (hasNumericSizes) {
        const numericQtySizeMatches = [...cleanText.matchAll(/(?:^|[-\s])(\d)(\d{2})(?=[-\s]|$)/g)];
        if (numericQtySizeMatches.length > 0) {
          let numericFound = false;
          for (const match of numericQtySizeMatches) {
            const quantity = parseInt(match[1]);
            const size = match[2];
            // Yalnızca gerçekten variant olarak var olan numeric beden'leri kabul et
            if (availableSizesStr.includes(size) && quantity > 0 && quantity <= 9) {
              distribution[size] = quantity;
              numericFound = true;
              log(`Found numeric qty+size: ${quantity}-${size}`);
            }
          }
          if (numericFound && Object.keys(distribution).length > 0) {
            log('Distribution from numeric qty+size format:', distribution);
            return distribution;
          }
        }
      }
    }

    // YENİ: Parantez formatı - "(S-M)-3" veya "(S-M):3"
    const parenthesisPattern = /\(([A-Z]+(?:-[A-Z]+)*)\)\s*[-:=]\s*(\d+)/gi;
    const parenthesisMatches = [...cleanText.matchAll(parenthesisPattern)];

    if (parenthesisMatches.length > 0) {
      log('Parenthesis format detected');
      for (const match of parenthesisMatches) {
        const sizeName = match[1].toUpperCase();
        const quantity = parseInt(match[2]);
        if (quantity > 0 && quantity <= 100) {
          distribution[sizeName] = quantity;
          log(`Found parenthesis format: (${sizeName})-${quantity}`);
        }
      }

      if (Object.keys(distribution).length > 0) {
        log('Distribution from parenthesis format:', distribution);
        return distribution;
      }
    }

    // YENİ: Tire içeren beden isimleri - "S-M-3" veya "L-XL:2"
    // Önce tire içeren beden isimlerini kontrol et (availableSizes'dan gelecek)
    if (window.productVariantInfo && window.productVariantInfo.allSizes) {
      const availableSizeNames = window.productVariantInfo.allSizes.map(s => s.toUpperCase());
      log('Checking compound size names:', availableSizeNames);

      // Tire içeren beden isimleri varsa onları dene
      for (const sizeName of availableSizeNames) {
        if (sizeName.includes('-')) {
          // "S-M" gibi tire içeren beden için pattern: "S-M-3" veya "S-M:3"
          const escapedSize = sizeName.replace(/-/g, '\\-');
          const compoundPattern = new RegExp(`(${escapedSize})\\s*[-:=]\\s*(\\d+)`, 'gi');
          const compoundMatch = cleanText.match(compoundPattern);

          if (compoundMatch) {
            const qtyMatch = compoundMatch[0].match(/(\d+)$/);
            if (qtyMatch) {
              const quantity = parseInt(qtyMatch[1]);
              if (quantity > 0 && quantity <= 100) {
                distribution[sizeName] = quantity;
                log(`Found compound size: ${sizeName}-${quantity}`);
              }
            }
          }
        }
      }

      if (Object.keys(distribution).length > 0) {
        log('Distribution from compound size names:', distribution);
        return distribution;
      }
    }

    // YENİ: Yaş bazlı beden formatı - "7-8-1  9-10-1  11-12-1  13-14-1"
    const ageRangeMatches = [...cleanText.matchAll(/(\d{1,2}-\d{1,2})-(\d{1,2})/g)];
    if (ageRangeMatches.length > 0) {
      log('Age-range format detected');
      for (const match of ageRangeMatches) {
        const ageRange = match[1]; // "7-8", "9-10"
        const quantity = parseInt(match[2]);
        if (quantity > 0 && quantity <= 100) {
          distribution[ageRange] = quantity;
          log(`Found age-range size: ${ageRange} = ${quantity}`);
        }
      }
      if (Object.keys(distribution).length > 0) {
        log('Distribution from age-range format:', distribution);
        return distribution;
      }
    }

    // YENİ: Yaş bazlı bedenler adet bilgisi olmadan - "7-8-9-10-11-12-13-14-15-16"
    // Sadece çift sayı dizisi varsa (3-4-5-6-7-8 gibi) → yaş aralıkları olarak grupla
    const ageOnlyMatch = cleanText.match(/^(\d{1,2}(?:-\d{1,2}){3,})$/);
    if (ageOnlyMatch) {
      const nums = ageOnlyMatch[1].split('-').map(Number);
      if (nums.length % 2 === 0) {
        log('Age-only format detected, grouping pairs');
        for (let i = 0; i < nums.length; i += 2) {
          const ageRange = `${nums[i]}-${nums[i + 1]}`;
          distribution[ageRange] = 1;
          log(`AUTO age-range: ${ageRange} = 1`);
        }
        if (Object.keys(distribution).length > 0) {
          return distribution;
        }
      }
    }

    // YENİ: Extended size formatı - "1XL1-2XL1-3XL1-4XL1" veya "5XL1-6XL1-7XL1"
    // Extended size + quantity: rakam + harf beden + rakam adet
    const extendedSizeQtyMatches = [...cleanText.matchAll(/(\d[A-Z]{1,3})(\d{1,2})(?=[-\s]|$)/gi)];
    if (extendedSizeQtyMatches.length > 0) {
      let extendedFound = false;
      for (const match of extendedSizeQtyMatches) {
        const sizeName = match[1].toUpperCase();
        const quantity = parseInt(match[2]);
        if (extendedSizes.has(sizeName) && quantity > 0 && quantity <= 100) {
          distribution[sizeName] = quantity;
          extendedFound = true;
          log(`Found extended size+qty: ${sizeName} = ${quantity}`);
        }
      }
      if (extendedFound && Object.keys(distribution).length > 0) {
        log('Distribution from extended size+qty format:', distribution);
        return distribution;
      }
    }

    // YENİ: Extended size isimleri adet bilgisi olmadan - "1XL-2XL-3XL-4XL"
    const extendedSizeOnlyMatches = [...cleanText.matchAll(/(\d[A-Z]{1,3})(?=[-\s]|$)/gi)];
    if (extendedSizeOnlyMatches.length > 0) {
      let extendedOnlyFound = false;
      for (const match of extendedSizeOnlyMatches) {
        const sizeName = match[1].toUpperCase();
        if (extendedSizes.has(sizeName)) {
          distribution[sizeName] = 1;
          extendedOnlyFound = true;
          log(`Found extended size (no qty): ${sizeName} = 1`);
        }
      }
      if (extendedOnlyFound && Object.keys(distribution).length > 0) {
        log('Distribution from extended size-only format:', distribution);
        return distribution;
      }
    }

    // YENİ: SIZE+QTY bitişik format - "S1-M1-L1-XL1" veya "S2-M1-L1"
    // Harf beden ismi + rakam adet (bitişik), tire veya boşluk ayraçlı
    const sizeQtyAttachedMatches = [...cleanText.matchAll(/([A-Z]{1,4})(\d{1,2})(?=[-\s]|$)/gi)];
    if (sizeQtyAttachedMatches.length > 0) {
      let attachedFound = false;
      for (const match of sizeQtyAttachedMatches) {
        const size = match[1].toUpperCase();
        const quantity = parseInt(match[2]);
        if (validSizes.has(size) && !excludedWords.has(size) && !extendedSizes.has(size) && quantity > 0 && quantity <= 100) {
          distribution[size] = quantity;
          attachedFound = true;
          log(`Found size+qty attached: ${size}${quantity}`);
        }
      }
      if (attachedFound && Object.keys(distribution).length > 0) {
        log('Distribution from size+qty attached format:', distribution);
        return distribution;
      }
    }

    // Önce "2S" gibi quantity-size formatını dene - SADECE GEÇERLİ BEDENLER
    const quantitySizeMatches = cleanText.matchAll(/(\d+)([A-Z]{1,4})/g);
    for (const match of quantitySizeMatches) {
      const quantity = parseInt(match[1]);
      const size = match[2].toUpperCase();
      // SADECE bilinen beden isimleri kabul et
      if (validSizes.has(size) && !excludedWords.has(size) && quantity > 0 && quantity <= 100) {
        distribution[size] = quantity;
        log(`Found quantity-size: ${quantity}${size}`);
      } else {
        log(`Skipped invalid size: ${size} from ${quantity}${size}`);
      }
    }

    if (Object.keys(distribution).length > 0) {
      log('Distribution from quantity-size format:', distribution);
      return distribution;
    }

    // YENİ: Sayısal birleşik qty+size formatı — "134-236-238-140" = 1×34, 2×36, 2×38, 1×40
    // size-quantity'den ÖNCE — yoksa "134-23" yanlış parse edilir
    {
      const chunks = cleanText.split(/\s*-\s*/).map(c => c.trim()).filter(Boolean);
      const allNumeric = chunks.length >= 2 && chunks.every(c => /^\d{3,4}$/.test(c));
      if (allNumeric) {
        const tempDist = {};
        let valid = true;
        for (const chunk of chunks) {
          let qty, size;
          if (chunk.length === 3) {
            qty = parseInt(chunk[0]);
            size = chunk.substring(1);
          } else {
            const sizeAs2 = parseInt(chunk.substring(2));
            if (sizeAs2 >= 20 && sizeAs2 <= 99) {
              qty = parseInt(chunk.substring(0, 2));
              size = chunk.substring(2);
            } else {
              qty = parseInt(chunk[0]);
              size = chunk.substring(1);
            }
          }
          const sizeNum = parseInt(size);
          if (qty > 0 && qty <= 100 && sizeNum >= 10 && sizeNum <= 200) {
            tempDist[size] = qty;
          } else {
            valid = false;
            break;
          }
        }
        if (valid && Object.keys(tempDist).length === chunks.length) {
          Object.assign(distribution, tempDist);
          log('Distribution from numeric concatenated qty+size:', distribution);
          return distribution;
        }
      }
    }

    // Sonra "S-2" gibi size-quantity formatını dene - GEÇERLİ BEDENLER + NUMERIC
    const sizeQuantityMatches = cleanText.matchAll(/([A-Z]{1,4}|\d{2,3})\s*-\s*(\d{1,2})/gi);
    for (const match of sizeQuantityMatches) {
      const size = match[1].toUpperCase();
      const quantity = parseInt(match[2]);
      const isNumericSize = /^\d{2,3}$/.test(size);
      // Harf bedenler: validSizes kontrolü, Numeric bedenler: direkt kabul
      if ((isNumericSize || (validSizes.has(size) && !excludedWords.has(size))) && quantity > 0 && quantity <= 100) {
        distribution[size] = quantity;
        log(`Found size-quantity: ${size}-${quantity}`);
      } else {
        log(`Skipped invalid size: ${size} from ${size}-${quantity}`);
      }
    }

    if (Object.keys(distribution).length > 0) {
      log('Distribution from size-quantity format:', distribution);
      return distribution;
    }

    // OPTIMIZE EDİLMİŞ: Manual parsing - sadece başarısızlık durumunda
    const parts = cleanText.split(/[\s\-]+/);
    log('Manual parsing parts:', parts);

    for (const part of parts) {
      if (!part) continue;

      // "2S" formatı - SADECE GEÇERLİ BEDENLER
      let match = part.match(/^(\d+)([A-Z]{1,4})$/i);
      if (match) {
        const quantity = parseInt(match[1]);
        const size = match[2].toUpperCase();
        if (validSizes.has(size) && !excludedWords.has(size) && quantity > 0 && quantity <= 100) {
          distribution[size] = quantity;
          log(`Manual quantity-size: ${size} = ${quantity}`);
        }
        continue;
      }

      // "S-2" veya "44-2" formatı - GEÇERLİ BEDENLER + NUMERIC
      match = part.match(/^([A-Z]{1,4}|\d{2,3})-(\d{1,2})$/i);
      if (match) {
        const size = match[1].toUpperCase();
        const quantity = parseInt(match[2]);
        const isNumericSize = /^\d{2,3}$/.test(size);
        if ((isNumericSize || (validSizes.has(size) && !excludedWords.has(size))) && quantity > 0 && quantity <= 100) {
          distribution[size] = quantity;
          log(`Manual size-quantity: ${size} = ${quantity}`);
        }
      }
    }

    // AUTO mode KAPATILDI — format tanınmıyorsa boş bırak (false-positive set davranışı önlenir)
    // Eski davranış normal multi-size ürünleri yanlışlıkla set yapıyordu
    if (Object.keys(distribution).length === 0) {
      log('No format matched — distribution boş (AUTO mode kapalı, set davranışı tetiklenmeyecek)');
    }

    log('Final distribution:', distribution);
    return distribution;
  }

  function parseVariantDistributions(distributionText) {
    const distributions = {};

    if (!distributionText) return distributions;

    const pairs = distributionText.split(',');

    pairs.forEach(pair => {
      const parts = pair.split('|');
      if (parts.length === 2) {
        const range = parts[0].trim();
        const multiplier = parseInt(parts[1]) || 1;
        distributions[range] = multiplier;
      }
    });

    return distributions;
  }
  function parsePaketYapisi(distributionText) {
    if (!distributionText) return 1;

    const cleanText = cleanupDistributionText(distributionText);
    log('Parsing Paket Yapısı text:', cleanText);

  // "Paket Yapısı: 1S+1M+1L+1XL" (TR) veya "Package Structure: ..." / "Package: ..." (EN)
  const paketMatch = cleanText.match(/(?:Paket Yapısı|Package Structure|Package)\s*:\s*(.+)/i);
  if (paketMatch) {
    const paketContent = paketMatch[1];
    // '+' ile ayrılmış kısımları say
    const parts = paketContent.split('+');
    const multiplier = parts.length;
    log(`Paket Yapısı detected: ${paketContent} -> multiplier: ${multiplier}`);
    return multiplier;
  }

  return 1;
}
  // STD format için özel stok kontrolü
  function calculateMaxSetsForSTD(stdMultiplier) {
    // STD formatında tek bir variant var, ilk variant'ı kullan
    if (allSizeVariants.length === 0) {
      return {
        maxSets: 0,
        limitingFactor: 'Variant bulunamadı',
        stockDetails: {},
        missingVariants: []
      };
    }

    const variantId = getSelectedOrFirstVariant();
    const stockInfo = sizeVariantStocks[variantId];

    if (!stockInfo) {
      return {
        maxSets: 0,
        limitingFactor: 'Stok bilgisi bulunamadı',
        stockDetails: {},
        missingVariants: []
      };
    }

    const rawStock = stockInfo.stock || 0;
    const consumed = getCartConsumed(variantId);
    const availableStock = Math.max(0, rawStock - consumed);
    const maxSets = Math.floor(availableStock / stdMultiplier);

    log(`STD Stock Check: ${rawStock} stok - ${consumed} sepette = ${availableStock} kalan / ${stdMultiplier} multiplier = ${maxSets} maksimum set`);

    return {
      maxSets: maxSets,
      limitingFactor: `${availableStock} stok / ${stdMultiplier} multiplier`,
      stockDetails: {
        'STD': {
          required: stdMultiplier,
          available: availableStock,
          variantId: variantId
        }
      },
      missingVariants: []
    };
  }

  // Darboğaz stok kontrolü fonksiyonu
  function calculateMaxSetsFromStock(distribution) {
    const isSTDFormat = Object.keys(distribution).length === 1 && Object.keys(distribution)[0] === 'STD';

    if (isSTDFormat) {
      const stdMultiplier = distribution['STD'];
      return calculateMaxSetsForSTD(stdMultiplier);
    }

    // LOT BAZLI: ideal'e tam bölünmese de kalan stok karma/kısmi lotlara dağıtılır.
    // maxSets artık "kaç LOT eklenebilir" demek (ideal + karma + kısmi toplamı).
    const idealNorm = normalizeDistribution(distribution);
    const stockBySize = buildProductStockBySize();
    const missingVariants = [];
    for (const size of Object.keys(distribution)) {
      if (!findVariantIdBySize(size)) missingVariants.push(size);
    }
    const lots = computeLots(idealNorm, stockBySize);
    log(`calculateMaxSetsFromStock — ${lots.length} lot:`, lots.map(l => l.type));

    return {
      maxSets: lots.length,
      lots: lots,
      limitingFactor: lots.length > 0 ? `${lots.length} lot eklenebilir` : 'Stok yok',
      stockDetails: {},
      missingVariants: missingVariants
    };
  }

  // Eksik varyant kontrolü
  function checkMissingVariants(distribution) {
    const missingVariants = [];

    for (const size of Object.keys(distribution)) {
      const variantId = findVariantIdBySize(size);
      if (!variantId) {
        missingVariants.push(size);
      }
    }

    return missingVariants;
  }

  // Set bütünlüğü kontrolü
  function validateSetIntegrity(userQuantity, distribution) {
    const stockAnalysis = calculateMaxSetsFromStock(distribution);
    const missingVariants = checkMissingVariants(distribution);

    const result = {
      isValid: true,
      maxPossibleSets: stockAnalysis.maxSets,
      requestedSets: userQuantity,
      canFulfill: userQuantity <= stockAnalysis.maxSets,
      missingVariants: missingVariants,
      limitingFactor: stockAnalysis.limitingFactor,
      stockDetails: stockAnalysis.stockDetails,
      errors: []
    };

    // Lot bazlı: ideal'deki bir beden eksik olsa bile karma/kısmi lotlar satılabilir.
    // Bu yüzden eksik varyant TEK BAŞINA engellemez — sadece hiç lot yoksa engellenir.
    if (stockAnalysis.maxSets <= 0) {
      result.isValid = false;
      result.errors.push('Bu ürün tükendi.');
    } else if (userQuantity > stockAnalysis.maxSets) {
      result.isValid = false;
      result.errors.push(`Maksimum ${stockAnalysis.maxSets} lot eklenebilir.`);
    }

    log('Set Integrity Check (lot):', result);
    return result;
  }

  // Quantity input'ları maksimum değerle sınırla
  function limitQuantityInputs() {
    if (isBedenSetiFormat) {
      // Beden seti formatında variant bazlı sınırlama
      const selectedVariant = getSelectedVariant();
      if (selectedVariant) {
        const variantSize = getVariantSize(selectedVariant);
        let multiplier = 1;

        for (const [range, rangeMultiplier] of Object.entries(variantDistributions)) {
          if (isSizeInRange(variantSize, range)) {
            multiplier = rangeMultiplier;
            break;
          }
        }

        const stockInfo = sizeVariantStocks[selectedVariant];
        const maxSets = stockInfo ? Math.floor(stockInfo.stock / multiplier) : 0;

        setQuantityInputLimits(maxSets);
      }
    }
    else if (isPaketYapisiFormat) {
      if (allSizeVariants.length > 0) {
        const variantId = getSelectedOrFirstVariant();
        const stockInfo = sizeVariantStocks[variantId];
        const maxSets = stockInfo ? Math.floor(stockInfo.stock / paketMultiplier) : 0;
        setQuantityInputLimits(maxSets);
      }
    }
   else {
  // Traditional format için set bazlı sınırlama
  const isSTDFormat = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';

  if (isSTDFormat) {
    const stdMultiplier = sizeDistribution['STD'];
    log(`STD Format detected: multiplier = ${stdMultiplier}`);

    // STD için özel stok kontrolü
    const stockAnalysis = calculateMaxSetsForSTD(stdMultiplier);
    const maxSets = stockAnalysis.maxSets;

    log(`STD Max sets calculation: ${maxSets} sets allowed`);
    setQuantityInputLimits(maxSets);
  } else {
    const stockAnalysis = calculateMaxSetsFromStock(sizeDistribution);
    setQuantityInputLimits(stockAnalysis.maxSets);
  }
}
  }

  // Stok 0 ise "Sepete Ekle" butonunu "Tükendi"ye çevir + devre dışı bırak
  function updateAddToCartButton(maxValue) {
    const buttons = document.querySelectorAll('button[name="add"][data-add-to-cart]');
    buttons.forEach(btn => {
      const idle = btn.querySelector('[data-add-to-cart-idle]');
      if (maxValue <= 0) {
        if (idle && !btn.dataset.wholesaleOrigText) {
          btn.dataset.wholesaleOrigText = idle.textContent.trim();
        }
        if (idle) idle.textContent = 'TÜKENDİ';
        btn.disabled = true;
        btn.dataset.wholesaleSoldout = '1';
      } else if (btn.dataset.wholesaleSoldout) {
        // Sadece BİZİM devre dışı bıraktıklarımızı geri aç
        if (idle && btn.dataset.wholesaleOrigText) {
          idle.textContent = btn.dataset.wholesaleOrigText;
        }
        btn.disabled = false;
        delete btn.dataset.wholesaleSoldout;
      }
    });
  }

  function setQuantityInputLimits(maxValue) {
    // Stok 0 ise butonu "Tükendi" yap
    updateAddToCartButton(maxValue);

    // SADECE stok 0 ise wrapper'ı gizle. max=1 olsa bile input görünsün; + butonu zaten
    // updateQuantityButtons içinde currentQty >= maxValue ise disable ediliyor.
    // (Eskiden max<=1'de gizliyorduk; kullanıcı input'u göremiyordu, "tek tek ekliyorum"
    // şikayetine yol açıyordu.)
    const quantityWrappers = document.querySelectorAll(
      '.product-action-zone__quantity-wrapper, [id^="Quantity-Selector-"], [data-quantity-wrapper]'
    );
    quantityWrappers.forEach(w => {
      if (maxValue <= 0) {
        w.style.display = 'none';
        w.setAttribute('aria-hidden', 'true');
        w.setAttribute('data-wholesale-hidden', 'max-0');
      } else {
        // Önceki gizleme'yi geri al (max-1 veya max-0)
        if (w.dataset.wholesaleHidden) {
          w.style.display = '';
          w.removeAttribute('aria-hidden');
          w.removeAttribute('data-wholesale-hidden');
        }
      }
    });

    const quantitySelectors = [
      'input[name="quantity"]',
      'input[type="number"][name="quantity"]',
      '.quantity-selector input',
      '.quantity__input',
      '.qty',
      '[data-quantity-input]'
    ];

    quantitySelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach(input => {
        if (input.type === 'number' || input.type === 'text') {
          input.setAttribute('max', maxValue);

          // Mevcut değer maksimumdan büyükse düzelt
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > maxValue) {
            input.value = Math.max(1, maxValue);

            // Change event'ini tetikle
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
          }

          // SERT ENGELLİ: Input'a manuel yazımı engelle
          if (maxValue === 0) {
            input.value = 0;
            input.disabled = true;
            input.style.backgroundColor = '#f5f5f5';
            input.style.color = '#999';
          } else {
            // Klavye ve mouse ile artırma/azaltmayı engelle
            input.addEventListener('keydown', function (e) {
              // Arrow up ve Page up tuşlarını engelle
              if ((e.key === 'ArrowUp' || e.key === 'PageUp') && parseInt(this.value) >= maxValue) {
                e.preventDefault();
                showMaxLimitNotification(maxValue);
                return false;
              }
              // Arrow down sadece 1'den küçük olmasını engelle
              if ((e.key === 'ArrowDown' || e.key === 'PageDown') && parseInt(this.value) <= 1) {
                e.preventDefault();
                return false;
              }
            });

            // Mouse wheel ile artırmayı engelle
            input.addEventListener('wheel', function (e) {
              if (e.deltaY < 0 && parseInt(this.value) >= maxValue) { // Wheel up
                e.preventDefault();
                showMaxLimitNotification(maxValue);
                return false;
              }
            });

            // Manuel yazım kontrolü
            input.addEventListener('input', function (e) {
              let value = parseInt(this.value);
              if (isNaN(value) || value < 1) {
                this.value = 1;
              } else if (value > maxValue) {
                this.value = maxValue;
                showMaxLimitNotification(maxValue);
              }
            });
          }
        }
      });
    });

    // Plus/minus butonlarını kontrol et
    updateQuantityButtons(maxValue);

    log(`Quantity inputs limited to maximum: ${maxValue} sets`);
  }

  function updateQuantityButtons(maxValue) {
    const currentQuantity = getCurrentQuantity();

    // Plus butonlarını kontrol et - SERT ENGELLİ
    const plusButtons = document.querySelectorAll(
      '[name="plus"], .quantity__button[name="plus"], button[aria-label*="Increase"], .btn-number[data-type="plus"], .quantity-selector button:last-child'
    );

    plusButtons.forEach(btn => {
      const atLimit = (currentQuantity >= maxValue) || maxValue === 0;

      // Görsel olarak "pasif" görünür AMA tıklamayı ALIR.
      // ÖNEMLİ: disabled=true veya pointerEvents='none' KULLANMA — böyle bir buton
      // click olayını hiç almaz, dolayısıyla "stok sınırı" bildirimi ateşlenmez.
      // (Eski bug tam olarak buydu: kullanıcı max'ta "+"a basınca sessizce hiçbir şey olmuyordu.)
      btn.disabled = false;
      btn.style.pointerEvents = 'auto';
      btn.style.opacity = atLimit ? '0.3' : '1';
      btn.style.cursor = atLimit ? 'not-allowed' : 'pointer';
      btn.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      btn.title = atLimit ? `Maksimum ${maxValue} lot eklenebilir` : '';

      // Tek, doğru handler: sınırdaysa BİLDİRİM göster; değilse miktarı artır.
      // (Değerler tıklama anında input'tan CANLI okunur — dönüşte "+" bozulmaz.)
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      newBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const qInput = document.querySelector('input[name="quantity"]') ||
          newBtn.parentNode.querySelector('input[name="quantity"], input[type="number"]');
        const cur = qInput ? (parseInt(qInput.value) || 1) : currentQuantity;
        const mx = qInput ? (parseInt(qInput.getAttribute('max')) || maxValue) : maxValue;
        if (mx > 0 && cur < mx) {
          if (qInput) {
            qInput.value = cur + 1;
            qInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          showMaxLimitNotification(mx);
        }
        return false;
      });
    });

    // Minus butonlarını kontrol et
    const minusButtons = document.querySelectorAll(
      '[name="minus"], .quantity__button[name="minus"], button[aria-label*="Decrease"], .btn-number[data-type="minus"], .quantity-selector button:first-child'
    );

    minusButtons.forEach(btn => {
      if (currentQuantity <= 1 || maxValue === 0) {
        btn.disabled = true;
        btn.style.opacity = '0.3';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
      } else {
        btn.disabled = false;
        btn.style.opacity = '1';
        btn.style.cursor = 'pointer';
        btn.style.pointerEvents = 'auto';
      }
    });
  }

  // Sınır uyarısı göster
  function showQuantityLimitWarning(maxValue) {
  // Mevcut uyarıyı kaldır
  const existingWarning = document.querySelector('.quantity-limit-warning');
  if (existingWarning) {
    existingWarning.remove();
  }

  const warning = document.createElement('div');
  warning.className = 'quantity-limit-warning';
  warning.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
    z-index: 99999;
    animation: slideInShake 0.5s ease-out;
    font-size: 14px;
    font-weight: 500;
    max-width: 300px;
    display: flex;
    align-items: center;
    gap: 10px;
  `;

  const isSTDFormat = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';
  let message;

  if (maxValue === 0) {
    message = 'Bu ürün tükendi.';
  } else {
    message = `Maksimum ${maxValue} lot eklenebilir.`;
  }

  warning.innerHTML = `<span>${message}</span>`;

  document.body.appendChild(warning);

  // 3 saniye sonra kaldır
  setTimeout(() => {
    if (warning.parentNode) {
      warning.style.animation = 'slideOutShake 0.3s ease-in';
      setTimeout(() => warning.remove(), 300);
    }
  }, 3000);
}

  function moveSetInfoToCorrectPosition() {
    const setInfoElement = document.getElementById('setMultiplierInfo');
    if (!setInfoElement) {
      log('Set info element not found, will be created later');
      return;
    }

    const priceSelectors = [
      '.price:not(.price--sold-out)',
      '.product-price',
      '.product__price',
      '.product-form__price',
      '[data-price]',
      '[data-product-price]',
      '.money',
      '.price-item',
      '.price-wrapper',
      '.product-price-wrapper'
    ];

    let priceElement = null;

    for (const selector of priceSelectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        priceElement = Array.from(elements).find(el => {
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        });
        if (priceElement) {
          log(`Found price element with selector: ${selector}`);
          break;
        }
      }
    }

    if (!priceElement) {
      const productForm = document.querySelector('form[action*="/cart/add"], .product-form');
      if (productForm) {
        priceElement = productForm.querySelector('.price, [class*="price"]');
        if (priceElement) {
          log('Found price element in product form');
        }
      }
    }

    if (priceElement) {
      priceElement.parentNode.insertBefore(setInfoElement, priceElement.nextSibling);
      log('Set info moved to position after price element');
    } else {
      log('Price element not found, keeping set info in original position');
      const productForm = document.querySelector('form[action*="/cart/add"], .product-form');
      if (productForm) {
        productForm.appendChild(setInfoElement);
        log('Set info moved to end of product form');
      }
    }
  }

  function updateSetInfoDisplay() {
    const setInfoText = document.getElementById('setInfoText');
    const setInfoDetails = document.getElementById('setInfoDetails');

    if (!setInfoText || !setInfoDetails) return;

    if (isBedenSetiFormat) {
      setInfoText.innerHTML = isEN()
        ? '<strong>Size Set:</strong> Different quantities will be added to your cart based on the size you choose.'
        : '<strong>Beden Seti:</strong> Seçtiğiniz bedene göre otomatik olarak farklı miktarlarda ürün sepetinize eklenecektir.';

      let detailsHtml = '<div style="margin-top: 5px;">';
      for (const [range, multiplier] of Object.entries(variantDistributions)) {
        const line = isEN()
          ? `if you select <strong>${multiplier} pcs</strong> will be added`
          : `seçerseniz → <strong>${multiplier} adet</strong> ürün eklenecek`;
        detailsHtml += `<div style="margin: 2px 0;"><span style="color: #1a1a1a; font-weight: 500;">${range}</span> ${line}</div>`;
      }
      detailsHtml += '</div>';
      setInfoDetails.innerHTML = detailsHtml;

    }
    else if (isPaketYapisiFormat) {
      setInfoText.innerHTML = `<strong>${T('packageProductLabel')}</strong> ${T('stdPerOrder', paketMultiplier)}`;
      setInfoDetails.innerHTML = `<div style="margin-top: 5px; color: #666;"><small>${T('soldAsPackage')}</small></div>`;
    }
    else {
      const totalItems = Object.values(sizeDistribution).reduce((sum, qty) => sum + qty, 0);

      const isSTDFormat = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';

      if (isSTDFormat) {
        setInfoText.innerHTML = `<strong>${T('setProductLabel')}</strong> ${T('stdPerOrder', totalItems)}`;
        setInfoDetails.innerHTML = `<div style="margin-top: 5px; color: #666;"><small>${T('soldAsSet')}</small></div>`;
      } else {
        // Stok analizi ekle
        const stockAnalysis = calculateMaxSetsFromStock(sizeDistribution);

        setInfoText.innerHTML = `<strong>${T('setProductLabel')}</strong> ${T('sizeSetPerOne', totalItems)}`;

        // Stok tükendiyse tüm set bilgi kutusunu gizle — buton zaten "Tükendi" oluyor
        const infoBox = document.getElementById('setMultiplierInfo');
        if (stockAnalysis.maxSets <= 0) {
          if (infoBox) infoBox.style.display = 'none';
          setInfoDetails.innerHTML = '';
        } else {
          if (infoBox) infoBox.style.display = '';
          let detailsHtml = `<div style="margin-top: 5px;"><strong>${T('distribution')}</strong> `;
          const distributionParts = [];
          for (const [size, qty] of Object.entries(sizeDistribution)) {
            distributionParts.push(`${size} (${T('pcsSuffix', qty)})`);
          }
          detailsHtml += distributionParts.join(', ') + '</div>';

          detailsHtml += `<div style="margin-top: 8px; padding: 6px; background: #e8f5e8; border-radius: 4px; border-left: 3px solid #4caf50;">
            <strong>${T('maxAddable', stockAnalysis.maxSets)}</strong>
            <br><small>${T('mixedNote')}</small>
          </div>`;

          setInfoDetails.innerHTML = detailsHtml;
        }
      }
    }
  }

  function getCurrentQuantity() {
    const quantitySelectors = [
      'input[name="quantity"]',
      'input[type="number"][name="quantity"]',
      '.quantity-selector input',
      '.quantity__input',
      '.qty',
      '[data-quantity-input]'
    ];

    for (const selector of quantitySelectors) {
      const input = document.querySelector(selector);
      if (input && input.value) {
        const value = parseInt(input.value) || 1;
        log(`Found quantity input: ${selector} = ${value}`);
        return Math.max(1, value);
      }
    }

    log('No quantity input found, defaulting to 1');
    return 1;
  }

  function updateSetInfoOnQuantityChange(quantity) {
    const setInfoDetails = document.getElementById('setInfoDetails');
    if (!setInfoDetails) return;

    quantity = Math.max(1, parseInt(quantity) || 1);

    // Maksimum sınırları kontrol et ve güncelle
    limitQuantityInputs();

    if (isBedenSetiFormat) {
      const selectedVariant = getSelectedVariant();
      if (selectedVariant) {
        const variantSize = getVariantSize(selectedVariant);
        let selectedRange = null;
        let multiplier = 1;

        for (const [range, rangeMultiplier] of Object.entries(variantDistributions)) {
          if (isSizeInRange(variantSize, range)) {
            selectedRange = range;
            multiplier = rangeMultiplier;
            break;
          }
        }

        if (selectedRange) {
          const finalQuantity = quantity * multiplier;
          const stockInfo = sizeVariantStocks[selectedVariant];
          const maxSets = stockInfo ? Math.floor(stockInfo.stock / multiplier) : 0;

          setInfoDetails.innerHTML = `<div style="margin-top: 5px; padding: 8px; background: #f0f0f0; border-radius: 4px; border: 1px solid #1a1a1a;">
            <strong>${T('yourSelection')}</strong> ${selectedRange} → <span style="color: #1a1a1a; font-weight: bold;">${T('addedWillBe', quantity, multiplier, finalQuantity)}</span>
            <div style="margin-top: 4px; font-size: 12px; color: #666;">
              ${T('moreAddable', maxSets, maxSets * multiplier, T('setUnit'))}
            </div>
          </div>`;
        }
      }
    }
    else if (isPaketYapisiFormat) {
      const finalTotal = quantity * paketMultiplier;
      const variantId = getSelectedOrFirstVariant();
      const stockInfo = sizeVariantStocks[variantId];
      const maxSets = stockInfo ? Math.floor(stockInfo.stock / paketMultiplier) : 0;

      setInfoDetails.innerHTML = `<div style="margin-top: 5px; padding: 8px; background: #f0f0f0; border-radius: 4px; border: 1px solid #1a1a1a;">
    <strong>${T('addingToCart')}</strong> <span style="color: #1a1a1a; font-weight: bold;">${T('pkgAdding', quantity, paketMultiplier, finalTotal)}</span>
    <div style="margin-top: 4px; font-size: 12px; color: #666;">
      ${T('moreAddable', maxSets, maxSets * paketMultiplier, T('packageUnit'))}
    </div>
  </div>`;
    }
    else {
      const totalPerSet = Object.values(sizeDistribution).reduce((sum, qty) => sum + qty, 0);
      const finalTotal = quantity * totalPerSet;

      const isSTDFormat = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';

      if (isSTDFormat) {
        const stdQuantity = sizeDistribution['STD'];
        const variantId = getSelectedOrFirstVariant();
        const stockInfo = sizeVariantStocks[variantId];
        const maxSets = stockInfo ? Math.floor(stockInfo.stock / stdQuantity) : 0;

        setInfoDetails.innerHTML = `<div style="margin-top: 5px; padding: 8px; background: #f0f0f0; border-radius: 4px; border: 1px solid #1a1a1a;">
          <strong>${T('addingToCart')}</strong> <span style="color: #1a1a1a; font-weight: bold;">${T('stdAdding', quantity, totalPerSet, finalTotal)}</span>
          <div style="margin-top: 4px; font-size: 12px; color: #666;">
            ${T('moreAddable', maxSets, maxSets * totalPerSet, T('setUnit'))}
          </div>
        </div>`;
      } else {
        // LOT BAZLI gösterim — Set İçerik Bilgisi etiketiyle
        const validation = validateSetIntegrity(quantity, sizeDistribution);
        const idealText = Object.entries(sizeDistribution).map(([s, q]) => `${s}×${q}`).join(' · ');
        const lotTotal = quantity * totalPerSet;

        // Stok tükendiyse tüm set bilgi kutusunu gizle — buton zaten "Tükendi" oluyor
        const infoBox = document.getElementById('setMultiplierInfo');
        if (!validation.isValid) {
          if (infoBox) infoBox.style.display = 'none';
          setInfoDetails.innerHTML = '';
        } else {
          if (infoBox) infoBox.style.display = '';
          let detailsHtml = `<div style="margin-top: 5px; padding: 8px; background: #f0f0f0; border-radius: 4px; border: 1px solid #1a1a1a;">`;
          detailsHtml += `<strong>${T('addingToCart')}</strong> <span style="color: #1a1a1a; font-weight: bold;">${T('lotAdding', quantity, lotTotal)}</span><br>`;
          detailsHtml += `<small style="color: #666;">${T('lotContents', idealText)}</small>`;
          detailsHtml += `<br><small style="color: #666; margin-top: 4px; display: block;">${T('maxAddableShort', validation.maxPossibleSets)}</small>`;
          detailsHtml += '</div>';
          setInfoDetails.innerHTML = detailsHtml;
        }
      }
    }
  }

  function getSelectedVariant() {
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      return variantSelect.value;
    }

    const checkedVariant = document.querySelector('input[name="id"]:checked');
    if (checkedVariant) {
      return checkedVariant.value;
    }

    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      return variantInput.value;
    }

    return null;
  }

  function getSelectedOrFirstVariant() {
    const sel = getSelectedVariant();
    if (sel) {
      const match = allSizeVariants.find(v => String(v) === String(sel));
      if (match !== undefined) return match;
    }
    return allSizeVariants[0];
  }

  function setupQuantityListener() {
    const quantitySelectors = [
      'input[name="quantity"]',
      'input[type="number"][name="quantity"]',
      '.quantity-selector input',
      '.quantity__input',
      '.qty',
      '[data-quantity-input]'
    ];

    quantitySelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach(input => {
        ['input', 'change', 'keyup', 'blur'].forEach(eventType => {
          input.addEventListener(eventType, (e) => {
            // OPTIMIZE EDİLMİŞ: Debounce ile tekrar eden event'leri engelle
            debounce('quantityChange', () => {
              let quantity = parseInt(e.target.value) || 1;

              // Maximum değeri kontrol et
              const maxValue = parseInt(e.target.getAttribute('max'));
              if (maxValue && quantity > maxValue) {
                quantity = maxValue;
                e.target.value = maxValue;
                showMaxLimitNotification(maxValue);
              }

              // Minimum 1 olması gerekiyor (max 0 değilse)
              if (quantity < 1 && maxValue !== 0) {
                quantity = 1;
                e.target.value = 1;
              }

              log(`Quantity changed via ${eventType}: ${quantity}`);
              updateSetInfoOnQuantityChange(quantity);
            }, 200);
          });
        });
      });
    });

    const variantSelectors = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        const currentQuantity = getCurrentQuantity();
        log(`Variant changed, updating with quantity: ${currentQuantity}`);

        // Variant değiştiğinde sınırları yeniden hesapla
        limitQuantityInputs();
        updateSetInfoOnQuantityChange(currentQuantity);
      });
    });

    // Renk değişikliğinde stok/lot hesabını yenile — seçili renk havuzu değişir
    const colorRadios = document.querySelectorAll(
      'fieldset input[type="radio"][name*="enk" i], fieldset input[type="radio"][name*="olor" i]'
    );
    colorRadios.forEach(r => {
      r.addEventListener('change', () => {
        const currentQuantity = getCurrentQuantity();
        log(`Color changed, recomputing with quantity: ${currentQuantity}`);
        limitQuantityInputs();
        updateSetInfoOnQuantityChange(currentQuantity);
      });
    });

    // Plus/minus butonları için SERT event listener'lar
    const plusButtons = document.querySelectorAll(
      '[name="plus"], .quantity__button[name="plus"], button[aria-label*="Increase"], .btn-number[data-type="plus"], .quantity-selector button:last-child'
    );

    plusButtons.forEach(btn => {
      // Tüm mevcut event listener'ları kaldır
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const quantityInput = document.querySelector('input[name="quantity"]') ||
          e.target.parentNode.querySelector('input[name="quantity"], input[type="number"]');

        if (quantityInput) {
          const currentValue = parseInt(quantityInput.value) || 1;
          const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;

          if (currentValue < maxValue && maxValue > 0) {
            const newValue = currentValue + 1;
            quantityInput.value = newValue;

            const changeEvent = new Event('change', { bubbles: true });
            quantityInput.dispatchEvent(changeEvent);
          } else {
            showMaxLimitNotification(maxValue);
          }
        }

        return false;
      });
    });

    const minusButtons = document.querySelectorAll(
      '[name="minus"], .quantity__button[name="minus"], button[aria-label*="Decrease"], .btn-number[data-type="minus"], .quantity-selector button:first-child'
    );

    minusButtons.forEach(btn => {
      // Tüm mevcut event listener'ları kaldır
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const quantityInput = document.querySelector('input[name="quantity"]') ||
          e.target.parentNode.querySelector('input[name="quantity"], input[type="number"]');

        if (quantityInput) {
          const currentValue = parseInt(quantityInput.value) || 1;
          const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;

          if (currentValue > 1 && maxValue > 0) {
            const newValue = currentValue - 1;
            quantityInput.value = newValue;

            const changeEvent = new Event('change', { bubbles: true });
            quantityInput.dispatchEvent(changeEvent);
          }
        }

        return false;
      });
    });

    const observer = new MutationObserver((mutations) => {
      // OPTIMIZE EDİLMİŞ: Debounce ile tekrarlayan çağrıları engelle
      debounce('mutationObserver', () => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                const quantityInputs = node.querySelectorAll ? node.querySelectorAll(quantitySelectors.join(',')) : [];
                quantityInputs.forEach(input => {
                  ['input', 'change'].forEach(eventType => {
                    input.addEventListener(eventType, (e) => {
                      let quantity = parseInt(e.target.value) || 1;

                      // Maximum değeri kontrol et
                      const maxValue = parseInt(e.target.getAttribute('max'));
                      if (maxValue && quantity > maxValue) {
                        quantity = maxValue;
                        e.target.value = maxValue;
                        showMaxLimitNotification(maxValue);
                      }

                      updateSetInfoOnQuantityChange(quantity);
                    });
                  });
                });
              }
            });
          }
        });
      }, 500);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Başlangıçta maksimum sınırları uygula
    setTimeout(() => {
      limitQuantityInputs();
    }, 100);
  }

  function normalizeSize(size) {
    return size.toString().toUpperCase().trim();
  }

  // TR karakter normalize + uppercase — renk karşılaştırmaları için
  function normalizeColor(c) {
    return (c || '').toString().trim().toUpperCase()
      .replace(/İ/g, 'I').replace(/I/g, 'I')
      .replace(/Ü/g, 'U').replace(/Ş/g, 'S')
      .replace(/Ç/g, 'C').replace(/Ğ/g, 'G').replace(/Ö/g, 'O');
  }

  // PDP'de seçili renk varyantını DOM'dan oku (radio veya select)
  function getSelectedColor() {
    const radio = document.querySelector('fieldset input[type="radio"]:checked[name*="enk" i], fieldset input[type="radio"]:checked[name*="olor" i], fieldset input[type="radio"]:checked[name*="olour" i]');
    if (radio) return radio.value || '';
    const sel = document.querySelector('select[name*="enk" i] option:checked, select[name*="olor" i] option:checked');
    return sel ? (sel.value || '') : '';
  }

  function findVariantIdBySize(targetSize) {
    const normalizedTarget = normalizeSize(targetSize);
    log(`🔍 Searching for variant with size: "${targetSize}" (normalized: "${normalizedTarget}")`);

    // Önce TAM EŞLEŞMEyE BAK - tire içeren beden isimleri için
    // RENK FİLTRESİ: seçili renk varsa önce o renge ait varyantı dene
    const selColor = normalizeColor(getSelectedColor());
    log('📦 Checking sizeVariantStocks:', Object.keys(sizeVariantStocks).length, 'variants', 'selColor:', selColor);
    for (const variantId in sizeVariantStocks) {
      const sizeInfo = sizeVariantStocks[variantId];
      if (!sizeInfo) continue;
      const sizeName = normalizeSize(sizeInfo.sizeName);
      log(`  - Variant ${variantId}: sizeName="${sizeInfo.sizeName}" color="${sizeInfo.colorName}" (normalized: "${sizeName}")`);
      if (sizeName !== normalizedTarget) continue;
      if (selColor && normalizeColor(sizeInfo.colorName) !== selColor) continue;
      log(`✓ Found variant ${variantId} for size ${targetSize} (exact match + color match)`);
      return variantId;
    }

    // Renk seçiliyse ama o renkte eşleşme yoksa: fallback — ilk eşleşen beden (renk yok say)
    if (selColor) {
      for (const variantId in sizeVariantStocks) {
        const si = sizeVariantStocks[variantId];
        if (si && normalizeSize(si.sizeName) === normalizedTarget) {
          log(`⚠ Color fallback: variant ${variantId} for size ${targetSize} (color "${si.colorName}" — secili renk eslesmedi)`);
          return variantId;
        }
      }
    }

    // Sonra availableSizes'ta ara
    log('📋 Checking availableSizes:', availableSizes);
    for (let i = 0; i < availableSizes.length; i++) {
      const availableSize = normalizeSize(availableSizes[i]);
      log(`  - Index ${i}: "${availableSizes[i]}" (normalized: "${availableSize}")`);
      if (availableSize === normalizedTarget && i < allSizeVariants.length) {
        log(`✓ Found variant ${allSizeVariants[i]} for size ${targetSize} at index ${i} (exact match)`);
        return allSizeVariants[i];
      }
    }

    // YENİ: Kısmi eşleşme - tire içeren beden isimleri için
    // Örnek: "XL" arıyoruz, "L-XL" varyantını bulabiliriz
    log('🔍 Trying partial match for compound sizes...');
    for (const variantId in sizeVariantStocks) {
      const sizeInfo = sizeVariantStocks[variantId];
      const sizeName = normalizeSize(sizeInfo.sizeName);

      // Tire içeren beden ismi mi?
      if (sizeName.includes('-')) {
        const sizeParts = sizeName.split('-').map(s => s.trim());
        log(`  - Checking compound size "${sizeName}": parts = [${sizeParts.join(', ')}]`);

        // Aranan beden, bileşenlerden biri mi?
        if (sizeParts.includes(normalizedTarget)) {
          log(`✓ Found variant ${variantId} for size ${targetSize} (partial match in "${sizeName}")`);
          return variantId;
        }
      }
    }

    // availableSizes'ta da kısmi eşleşme dene
    for (let i = 0; i < availableSizes.length; i++) {
      const availableSize = normalizeSize(availableSizes[i]);

      if (availableSize.includes('-')) {
        const sizeParts = availableSize.split('-').map(s => s.trim());

        if (sizeParts.includes(normalizedTarget) && i < allSizeVariants.length) {
          log(`✓ Found variant ${allSizeVariants[i]} for size ${targetSize} at index ${i} (partial match in "${availableSize}")`);
          return allSizeVariants[i];
        }
      }
    }

    logError(`❌ Variant NOT found for size: "${targetSize}" (normalized: "${normalizedTarget}")`);
    logError('Available sizeVariantStocks keys:', Object.keys(sizeVariantStocks));
    logError('Available sizes:', availableSizes);
    return null;
  }

  // CSS animasyonları ekle
  function addQuantityLimitStyles() {
    if (document.querySelector('#quantity-limit-styles')) return;

    const style = document.createElement('style');
    style.id = 'quantity-limit-styles';
    style.textContent = `
      @keyframes slideInShake {
        0% { 
          transform: translateX(100%) scale(0.8);
          opacity: 0;
        }
        50% {
          transform: translateX(-10px) scale(1.05);
          opacity: 1;
        }
        60% {
          transform: translateX(5px) scale(1.02);
        }
        70% {
          transform: translateX(-3px) scale(1.01);
        }
        100% { 
          transform: translateX(0) scale(1);
          opacity: 1;
        }
      }
      @keyframes slideOutShake {
        0% { 
          transform: translateX(0) scale(1);
          opacity: 1;
        }
        100% { 
          transform: translateX(100%) scale(0.8);
          opacity: 0;
        }
      }
      
      .quantity-limit-warning {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }
      
      /* Disabled buton efektleri */
      button:disabled.quantity-disabled {
        background: #f5f5f5 !important;
        color: #ccc !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
        opacity: 0.3 !important;
        transform: none !important;
      }
      
      /* Input disabled efektleri */
      input:disabled.quantity-disabled {
        background: #f5f5f5 !important;
        color: #999 !important;
        cursor: not-allowed !important;
        border-color: #ddd !important;
      }
    `;
    document.head.appendChild(style);
  }

  function forceMultiplier() {
    if (isInitialized) return;
    isInitialized = true;

    if (!initializeMultiplier()) {
      logError('Set multiplier initialization failed');
      return;
    }

    // CSS stilleri ekle
    addQuantityLimitStyles();

    // Debug: Başlangıç bilgilerini logla
    log('=== INITIALIZATION DEBUG ===');
    log('Size distribution:', sizeDistribution);
    log('Size variant stocks:', sizeVariantStocks);
    log('All size variants:', allSizeVariants);
    log('Is STD format:', Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD');

    if (Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD') {
      const stdMultiplier = sizeDistribution['STD'];
      const stockAnalysis = calculateMaxSetsForSTD(stdMultiplier);
      log('STD Stock Analysis:', stockAnalysis);
    }
    log('=== END DEBUG ===');

    const originalFetch = window.fetch;
    let interceptorApplied = false;

    if (!interceptorApplied) {
      window.fetch = async function (url, options = {}) {
        if (options.headers && options.headers[internalRequestFlag]) {
          delete options.headers[internalRequestFlag];
          return originalFetch.call(this, url, options);
        }

        if (typeof url === 'string' && url.includes('/cart/add')) {
          return handleSetCartRequest(originalFetch, url, options);
        }

        return originalFetch.call(this, url, options);
      };
      interceptorApplied = true;
      log('Fetch interceptor applied');
    }

    setTimeout(() => {
      setupQuantityListener();
      moveSetInfoToCorrectPosition();
      const initialQuantity = getCurrentQuantity();
      log(`Updating with initial quantity: ${initialQuantity}`);
      // Cart cache'i ilk yükle, sonra max'ları sepete göre uygula
      refreshCartConsumedCache(true).then(() => {
        updateSetInfoOnQuantityChange(initialQuantity);
        limitQuantityInputs();
      });
    }, 500);

    // Sepet güncellendiğinde cache'i yenile ve max input limitlerini sepete göre güncelle
    const refreshOnCart = () => {
      refreshCartConsumedCache(true).then(() => {
        try { limitQuantityInputs(); } catch (_) {}
        try { updateSetInfoDisplay(); } catch (_) {}
      });
    };
    document.addEventListener('cart:updated', refreshOnCart);
    document.addEventListener('cart:refresh', refreshOnCart);
    document.addEventListener('cart-updated', refreshOnCart);

    log('Set Multiplier active');
  }

  async function handleSetCartRequest(originalFetch, url, options) {
    const requestSignature = createRequestSignature(options.body);

    if (processedRequests.has(requestSignature)) {
      return new Response(JSON.stringify({
        status: 'success',
        message: 'Request already processed',
        duplicate: true
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    processedRequests.add(requestSignature);

    if (isProcessingRequest) {
      return new Response(JSON.stringify({
        status: 'success',
        message: 'Set işleniyor...',
        processing: true
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    isProcessingRequest = true;

    try {
      const userQuantity = getQuantityFromData(options.body);
      let addResults;

      if (isBedenSetiFormat) {
        const selectedVariantId = getVariantIdFromData(options.body);
        addResults = await addBedenSetiToCart(originalFetch, userQuantity, selectedVariantId, url, options);
      } else {
        addResults = await addTraditionalSetToCart(originalFetch, userQuantity, url, options);
      }

      if (addResults.success) {
        setTimeout(() => {
          triggerCartEvents({ item_count: addResults.totalAdded });
        }, 100);

        setTimeout(() => updateCartCount(), 300);

        setTimeout(() => {
          updateCartDrawer();
        }, 500);

        // KULLANICI BİLDİRİMİ: Sepete eklendi
        showAddedToCartNotification(userQuantity, addResults.totalAdded, isBedenSetiFormat ? 'Beden Seti' : (isPaketYapisiFormat ? 'Paket' : 'Set'));

        const formatInfo = isBedenSetiFormat ? ' (Beden Seti)' :
          isPaketYapisiFormat ? ' (Paket)' : ' (Set)';
        // SAHTE sections payload kaldirildi — product.js renderSections()
        // selector bulamayinca container.innerHTML = html ile #Cart-Drawer
        // DOM'unu ezerek mevcut sepet satirlarini (A urunu) gorsel olarak siliyordu.
        // updateCartDrawer 500ms sonra cart:refresh dispatch ediyor; sections
        // gondermedik icin product.js renderSections cagrilmaz, sepet temiz kalir.
        return new Response(JSON.stringify({
          status: 'success',
          message: `${userQuantity}${formatInfo} (${addResults.totalAdded} adet) ürün eklendi`,
          details: addResults.details,
          format: isBedenSetiFormat ? 'beden_seti' :
            isPaketYapisiFormat ? 'paket_yapisi' : 'traditional', id: Date.now(),
          quantity: addResults.totalAdded,
          variant_id: 'multiple',
          line_price: 0
        }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        logError('Set adding failed:', addResults.errors);

        // Hata mesajını kullanıcıya göster
        return new Response(JSON.stringify({
          status: 'error',
          message: (addResults.errors && addResults.errors[0]) || 'Ürün sepete eklenemedi.',
          description: (addResults.errors && addResults.errors[0]) || 'Ürün sepete eklenemedi.'
        }), {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        });
      }

    } catch (error) {
      logError('Set request error:', error);
      return originalFetch.call(this, url, options);

    } finally {
      // İşlem bayrağını hemen sıfırla (daha responsive olması için)
      setTimeout(() => {
        isProcessingRequest = false;
      }, 100);

      // Request signature'ı biraz sonra temizle (duplicate önleme için)
      setTimeout(() => {
        processedRequests.delete(requestSignature);
      }, 5000);
    }
  }

  async function addBedenSetiToCart(originalFetch, userQuantity, selectedVariantId, originalUrl, originalOptions) {
    let selectedRange = null;
    let multiplier = 1;

    const variantSize = getVariantSize(selectedVariantId);

    for (const [range, rangeMultiplier] of Object.entries(variantDistributions)) {
      if (isSizeInRange(variantSize, range)) {
        selectedRange = range;
        multiplier = rangeMultiplier;
        break;
      }
    }

    if (!selectedRange) {
      return {
        success: false,
        errors: ['Ürün eklenemedi.'],
        details: [],
        totalAdded: 0
      };
    }

    const finalQuantity = userQuantity * multiplier;

    // Cart-aware: sepette zaten olanı düş
    await refreshCartConsumedCache(true);
    const stockInfo = sizeVariantStocks[selectedVariantId];
    const rawStock = stockInfo ? (stockInfo.stock || 0) : 0;
    const consumed = getCartConsumed(selectedVariantId);
    const effectiveStock = Math.max(0, rawStock - consumed);
    if (!stockInfo || effectiveStock < finalQuantity) {
      return {
        success: false,
        errors: ['Bu ürün tükendi.'],
        details: [],
        totalAdded: 0
      };
    }

    try {
      const newRequestBody = createSizeRequestBody(originalOptions.body, selectedVariantId, finalQuantity);

      const sizeOptions = {
        ...originalOptions,
        body: newRequestBody,
        headers: {
          ...originalOptions.headers,
          [internalRequestFlag]: 'true'
        }
      };

      const response = await originalFetch.call(this, originalUrl, sizeOptions);

      if (response.ok) {
        return {
          success: true,
          errors: [],
          details: [`${selectedRange}: ${finalQuantity} adet`],
          totalAdded: finalQuantity
        };
      } else {
        const errorText = await response.text();
        return {
          success: false,
          errors: ['Ürün sepete eklenemedi.'],
          details: [],
          totalAdded: 0
        };
      }

    } catch (error) {
      return {
        success: false,
        errors: ['Bağlantı hatası, tekrar deneyin.'],
        details: [],
        totalAdded: 0
      };
    }
  }

  function getVariantSize(variantId) {
    const stockInfo = sizeVariantStocks[variantId];
    if (stockInfo) {
      return stockInfo.sizeName;
    }

    const variantIndex = allSizeVariants.indexOf(variantId);
    if (variantIndex !== -1 && variantIndex < availableSizes.length) {
      return availableSizes[variantIndex];
    }

    return 'UNKNOWN';
  }

  function isSizeInRange(size, range) {
    const cleanSize = size.replace(/[()]/g, '').trim();
    const cleanRange = range.trim();

    if (cleanSize === cleanRange) {
      return true;
    }

    const rangeParts = cleanRange.split('-');
    if (rangeParts.length !== 2) {
      return false;
    }

    const rangeStart = parseInt(rangeParts[0]);
    const rangeEnd = parseInt(rangeParts[1]);

    if (cleanSize.includes('-')) {
      const sizeParts = cleanSize.split('-');
      if (sizeParts.length === 2) {
        const sizeStart = parseInt(sizeParts[0]);
        const sizeEnd = parseInt(sizeParts[1]);
        return sizeStart === rangeStart && sizeEnd === rangeEnd;
      }
    } else {
      const sizeNum = parseInt(cleanSize);
      if (!isNaN(sizeNum) && !isNaN(rangeStart) && !isNaN(rangeEnd)) {
        return sizeNum >= rangeStart && sizeNum <= rangeEnd;
      }
    }

    return false;
  }

  async function addTraditionalSetToCart(originalFetch, userQuantity, originalUrl, originalOptions) {
    const results = {
      success: true,
      errors: [],
      details: [],
      totalAdded: 0
    };

    // Cart-aware: tüm stok kontrolleri sepetteki mevcut adetleri düşerek yapılır
    await refreshCartConsumedCache(true);

    const isSTDFormat = Object.keys(sizeDistribution).length === 1 && Object.keys(sizeDistribution)[0] === 'STD';

    if (isSTDFormat) {
      const stdQuantity = sizeDistribution['STD'];
      const finalQty = stdQuantity * userQuantity;

      // Kullanıcının seçtiği varyant (renk) formdan gelir — ilk varyanta düşmek
      // tüm renkleri "Siyah" olarak ekliyordu
      const submittedId = getVariantIdFromData(originalOptions.body);
      const variantId = allSizeVariants.find(v => String(v) === String(submittedId))
        || findVariantIdBySize('STD')
        || allSizeVariants[0];
      if (!variantId) {
        return {
          success: false,
          errors: ['Ürün eklenemedi.'],
          details: [],
          totalAdded: 0
        };
      }

      let hasStock = true;
      let effectiveStock = 999999;
      let rawStock = 999999;
      let consumed = 0;

      const stockInfo = sizeVariantStocks[variantId];
      if (stockInfo && stockInfo.stock !== undefined && stockInfo.stock !== null) {
        rawStock = stockInfo.stock;
        consumed = getCartConsumed(variantId);
        effectiveStock = Math.max(0, rawStock - consumed);
        if (effectiveStock < finalQty) {
          hasStock = false;
        }
      }

      if (!hasStock) {
        return {
          success: false,
          errors: ['Bu ürün tükendi.'],
          details: [],
          totalAdded: 0
        };
      }

      try {
        const newRequestBody = createSizeRequestBody(originalOptions.body, variantId, finalQty);

        const sizeOptions = {
          ...originalOptions,
          body: newRequestBody,
          headers: {
            ...originalOptions.headers,
            [internalRequestFlag]: 'true'
          }
        };

        log(`STD Format: Adding ${finalQty} items of variant ${variantId}`);

        const response = await originalFetch.call(this, originalUrl, sizeOptions);

        if (response.ok) {
          results.details.push(`STD: ${finalQty} adet`);
          results.totalAdded += finalQty;
          log(`STD Success: ${finalQty} items added`);
        } else {
          const errorText = await response.text();
          results.errors.push(`STD: HTTP ${response.status}`);
          logError(`STD failed: HTTP ${response.status}`, errorText);
        }

      } catch (error) {
        results.errors.push(`STD: ${error.message}`);
        logError('STD network error:', error);
      }

      // STD format işlemi tamamlandı, fonksiyondan çık
      return results;
    }

    const isPaketFormat = isPaketYapisiFormat;

    if (isPaketFormat) {
      const finalQty = paketMultiplier * userQuantity;

      const submittedIdPaket = getVariantIdFromData(originalOptions.body);
      const variantId = allSizeVariants.find(v => String(v) === String(submittedIdPaket))
        || allSizeVariants[0];
      if (!variantId) {
        return {
          success: false,
          errors: ['Ürün eklenemedi.'],
          details: [],
          totalAdded: 0
        };
      }

      const stockInfo = sizeVariantStocks[variantId];
      if (stockInfo && stockInfo.stock !== undefined && stockInfo.stock !== null) {
        const rawStock = stockInfo.stock;
        const consumed = getCartConsumed(variantId);
        const effectiveStock = Math.max(0, rawStock - consumed);
        if (effectiveStock < finalQty) {
          return {
            success: false,
            errors: ['Bu ürün tükendi.'],
            details: [],
            totalAdded: 0
          };
        }
      }

      try {
        const newRequestBody = createSizeRequestBody(originalOptions.body, variantId, finalQty);

        const sizeOptions = {
          ...originalOptions,
          body: newRequestBody,
          headers: {
            ...originalOptions.headers,
            [internalRequestFlag]: 'true'
          }
        };

        log(`Paket Format: Adding ${finalQty} items of variant ${variantId}`);

        const response = await originalFetch.call(this, originalUrl, sizeOptions);

        if (response.ok) {
          results.details.push(`Paket: ${finalQty} adet`);
          results.totalAdded += finalQty;
          log(`Paket Success: ${finalQty} items added`);
        } else {
          const errorText = await response.text();
          results.errors.push(`Paket: HTTP ${response.status}`);
          logError(`Paket failed: HTTP ${response.status}`, errorText);
        }

      } catch (error) {
        results.errors.push(`Paket: ${error.message}`);
        logError('Paket network error:', error);
      }

      // Paket format işlemi tamamlandı, fonksiyondan çık
      return results;
    }

    // Traditional set format — ATOMIC ekleme (tek HTTP request, ya hep ya hiç)
    if (!isSTDFormat && !isPaketFormat) {
      const validation = validateSetIntegrity(userQuantity, sizeDistribution);
      if (!validation.isValid) {
        return { success: false, errors: validation.errors, details: [], totalAdded: 0 };
      }

      // LOT BİRLEŞİMİ: ilk userQuantity lotun toplamı (ideal → karma → kısmi sırası).
      // computeLots stoğu zaten dikkate aldığı için union otomatik stok-sınırlı.
      const idealNorm = normalizeDistribution(sizeDistribution);
      const stockBySize = buildProductStockBySize();
      const lots = computeLots(idealNorm, stockBySize);
      const union = mergeLotsUnion(lots, userQuantity);
      log(`Lot ekleme — ${userQuantity} lot, birleşim:`, union);

      const itemsToAdd = [];
      for (const [size, qty] of Object.entries(union)) {
        if (qty <= 0) continue;
        const variantId = findVariantIdBySize(size);
        if (!variantId) {
          return {
            success: false,
            errors: ['Ürün eklenemedi.'],
            details: [],
            totalAdded: 0
          };
        }
        itemsToAdd.push({ id: variantId, quantity: qty, size, finalQty: qty });
      }

      if (itemsToAdd.length === 0) {
        return {
          success: false,
          errors: ['Bu ürün tükendi.'],
          details: [],
          totalAdded: 0
        };
      }

      log(`Atomic add: ${itemsToAdd.length} bedenler tek istekte gönderiliyor`);

      // Shopify /cart/add.js items[] formatı — atomik ekleme
      try {
        const atomicBody = JSON.stringify({
          items: itemsToAdd.map(it => ({ id: it.id, quantity: it.quantity }))
        });
        const atomicOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            [internalRequestFlag]: 'true'
          },
          body: atomicBody
        };
        const response = await originalFetch.call(this, originalUrl, atomicOptions);

        if (response.ok) {
          for (const it of itemsToAdd) {
            results.details.push(`${it.size}: ${it.finalQty} adet`);
            results.totalAdded += it.finalQty;
          }
          log(`Atomic add SUCCESS: ${results.totalAdded} adet, ${itemsToAdd.length} beden`);
        } else {
          const errorText = await response.text();
          let humanMsg = 'Sepete ekleme başarısız oldu.';
          try {
            const parsed = JSON.parse(errorText);
            if (parsed && (parsed.description || parsed.message)) {
              humanMsg = parsed.description || parsed.message;
            }
          } catch (_) {}
          results.errors.push(humanMsg);
          results.success = false;
          logError(`Atomic add failed: HTTP ${response.status}`, errorText);
        }
      } catch (error) {
        results.errors.push(`Atomic add network error: ${error.message}`);
        results.success = false;
        logError('Atomic add network error:', error);
      }

      return results;
    }

    // Hiçbir format eşleşmedi, boş sonuç döndür
    return results;
  }

  function createSizeRequestBody(originalBody, variantId, quantity) {
    if (originalBody instanceof FormData) {
      const newFormData = new FormData();
      for (let [key, value] of originalBody.entries()) {
        if (key === 'id' || key === 'variant_id') {
          newFormData.append(key, variantId);
        } else if (key === 'quantity') {
          newFormData.append(key, quantity);
        } else {
          newFormData.append(key, value);
        }
      }

      if (!newFormData.has('id')) {
        newFormData.append('id', variantId);
      }
      if (!newFormData.has('quantity')) {
        newFormData.append('quantity', quantity);
      }

      return newFormData;
    }

    if (typeof originalBody === 'string') {
      try {
        const json = JSON.parse(originalBody);
        json.id = variantId;
        json.quantity = quantity;
        return JSON.stringify(json);
      } catch (e) {
        let result = originalBody;
        result = result.replace(/[?&]id=([^&]+)/g, `id=${variantId}`);
        result = result.replace(/[?&]quantity=([^&]+)/g, `quantity=${quantity}`);
        if (!result.includes('id=')) {
          result += (result.includes('?') ? '&' : '?') + `id=${variantId}`;
        }
        if (!result.includes('quantity=')) {
          result += `&quantity=${quantity}`;
        }
        return result;
      }
    }

    const fallbackFormData = new FormData();
    fallbackFormData.append('id', variantId);
    fallbackFormData.append('quantity', quantity);
    return fallbackFormData;
  }

  function createRequestSignature(data) {
    const userQty = getQuantityFromData(data);
    const timestamp = Date.now();
    const baseSignature = `qty-${userQty}-time-${Math.floor(timestamp / 5000)}`;
    return baseSignature;
  }

  function getQuantityFromData(data) {
    if (!data) return 1;

    let quantity = 1;

    try {
      if (data instanceof FormData) {
        quantity = parseInt(data.get('quantity')) || 1;
      } else if (typeof data === 'string') {
        try {
          const json = JSON.parse(data);
          quantity = parseInt(json.quantity) || 1;
        } catch (e) {
          const match = data.match(/quantity=(\d+)/);
          quantity = match ? parseInt(match[1]) : 1;
        }
      }
    } catch (e) {
      quantity = 1;
    }

    quantity = Math.max(1, Math.floor(Math.abs(quantity)));
    return quantity;
  }

  function getVariantIdFromData(data) {
    if (!data) return 'unknown';

    try {
      if (data instanceof FormData) {
        return data.get('id') || data.get('variant_id') || 'unknown';
      }

      if (typeof data === 'string') {
        try {
          const json = JSON.parse(data);
          return json.id || json.variant_id || 'unknown';
        } catch (e) {
          const idMatch = data.match(/[?&]id=([^&]+)/);
          return idMatch ? idMatch[1] : 'unknown';
        }
      }
    } catch (e) {
      return 'unknown';
    }

    return 'unknown';
  }

  function triggerCartEvents(cart) {
    try {
      // Validate cart object
      const safeCart = cart || {};
      const itemCount = safeCart.item_count || 0;

      const cartUpdateEvent = new CustomEvent('cart:updated', {
        detail: { cart: safeCart }
      });
      document.dispatchEvent(cartUpdateEvent);

      const events = [
        'cart:update',
        'cart:refresh',
        'cartUpdated',
        'cart-updated',
        'ajaxCart:update'
      ];

      events.forEach(eventName => {
        try {
          const event = new CustomEvent(eventName, {
            detail: { cart: safeCart, itemCount: itemCount }
          });
          document.dispatchEvent(event);
        } catch (e) {
          log('Error dispatching event:', eventName, e.message);
        }
      });

      if (window.jQuery && typeof window.jQuery === 'function') {
        try {
          window.jQuery(document).trigger('cart:updated', [safeCart]);
          window.jQuery(document).trigger('cartUpdated', [safeCart]);
        } catch (e) {
          log('Error triggering jQuery events:', e.message);
        }
      }
    } catch (e) {
      logError('Error in triggerCartEvents:', e.message);
    }
  }

  // ÇOK BELİRGİN ortak toast helper — top center, büyük, bounce animasyonlu
  function _wholesaleShowToast(opts) {
    const { id, title, message, icon, bgColor, fgColor, duration = 4000 } = opts;
    try {
      // Aynı tipten varsa kaldır
      document.querySelectorAll('.wholesale-toast-' + id).forEach(el => el.remove());

      const toast = document.createElement('div');
      toast.className = 'wholesale-toast wholesale-toast-' + id;
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.style.cssText = `
        position: fixed;
        top: 32px;
        left: 50%;
        transform: translateX(-50%) translateY(-120px) scale(0.95);
        z-index: 2147483647;
        min-width: 360px;
        max-width: min(560px, 92vw);
        padding: 22px 28px;
        border-radius: 12px;
        background: ${bgColor};
        color: ${fgColor};
        box-shadow: 0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.08);
        font-size: 16px;
        line-height: 1.45;
        display: flex;
        align-items: center;
        gap: 18px;
        opacity: 0;
        transition: opacity 350ms ease, transform 450ms cubic-bezier(0.34, 1.56, 0.64, 1);
        font-family: inherit;
        pointer-events: auto;
      `;
      toast.innerHTML = `
        ${icon ? `<div style="flex-shrink:0; width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center;">${icon}</div>` : ''}
        <div style="flex:1; min-width:0;">
          <div style="font-weight:800; letter-spacing:0.6px; text-transform:uppercase; font-size:12px; opacity:0.92; margin-bottom:4px;">${title}</div>
          <div style="font-weight:500; font-size:16px; line-height:1.4;">${message}</div>
        </div>
      `;
      document.body.appendChild(toast);

      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateX(-50%) translateY(0) scale(1)';
      });

      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(-30px) scale(0.95)';
        setTimeout(() => toast.remove(), 400);
      }, duration);
    } catch (e) {
      log('Toast hatası:', e);
    }
  }

  // Sepete eklendi bildirimi — siyah, sade
  function showAddedToCartNotification(setCount, totalAdded, formatName) {
    const unit = isEN() ? (Number(setCount) === 1 ? 'lot' : 'lots') : 'lot';
    _wholesaleShowToast({
      id: 'added',
      title: T('addedToastTitle'),
      message: isEN()
        ? `<strong>${setCount} ${unit}</strong> · ${totalAdded} items`
        : `<strong>${setCount} lot</strong> · ${totalAdded} ürün`,
      icon: '',
      bgColor: '#1a1a1a',
      fgColor: '#fff',
      duration: 3500
    });
  }

  // Max uyarısı — siyah, sade
  function showMaxLimitNotification(maxSets) {
    _wholesaleShowToast({
      id: 'max',
      title: maxSets > 0
        ? (isEN() ? 'Stock limit' : 'Stok sınırı')
        : (isEN() ? 'Sold out' : 'Tükendi'),
      message: maxSets > 0
        ? (isEN()
            ? `You can add at most <strong>${maxSets} ${maxSets === 1 ? 'lot' : 'lots'}</strong> of this product.`
            : `Bu üründen en fazla <strong>${maxSets} lot</strong> eklenebilir.`)
        : (isEN() ? 'This product is sold out.' : 'Bu ürün tükendi.'),
      icon: '',
      bgColor: '#1a1a1a',
      fgColor: '#fff',
      duration: 3500
    });
  }

  function updateCartDrawer() {
    try {
      // Tema ayarlarına saygı duy: cart_open_on_add=false veya cart_type=page ise drawer hiç açılmasın
      const header = document.querySelector('header-component');
      const cartOpenOnAdd = header?.dataset.cartOpenOnAdd !== 'false';
      const cartType = header?.dataset.cartType || 'drawer';
      if (!cartOpenOnAdd || cartType === 'page') {
        log('updateCartDrawer skipped: cart_open_on_add=false or cart_type=page');
        if (typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        return;
      }

      // Method 1: Try cart-drawer custom element
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          log('Cart drawer opened via custom element');
          return;
        } else if (typeof cartDrawer.show === 'function') {
          cartDrawer.show();
          log('Cart drawer opened via show method');
          return;
        }
      }

      // Method 2: Try theme cart drawer
      if (window.theme && window.theme.cartDrawer) {
        if (typeof window.theme.cartDrawer.open === 'function') {
          window.theme.cartDrawer.open();
          log('Cart drawer opened via theme object');
          return;
        }
        if (typeof window.theme.cartDrawer.refresh === 'function') {
          window.theme.cartDrawer.refresh();
          log('Cart drawer refreshed via theme object');
        }
      }

      // Method 3: Try Shopify theme events
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('cart:refresh'));
        log('Cart refresh event dispatched');
      }

      // Method 4: Try cart drawer via class selector
      const cartDrawerClass = document.querySelector('.cart-drawer, #cart-drawer');
      if (cartDrawerClass && typeof cartDrawerClass.classList !== 'undefined') {
        cartDrawerClass.classList.add('active', 'is-active', 'open');
        log('Cart drawer activated via class manipulation');
      }
    } catch (e) {
      logError('Error opening cart drawer:', e.message);
    }
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(cart => {
        if (!cart) {
          logError('Cart data is null or undefined');
          return;
        }

        const itemCount = cart.item_count || 0;
        const cartCounters = document.querySelectorAll(
          '.cart-count, [data-cart-count], .cart-item-count, #cart-icon-bubble, .cart-counter, #Cart-Icon-Bubble, [data-cart-badge]' /* 24 Eki Hasan: temadaki ikon #Cart-Icon-Bubble (büyük harf) → anında güncellenmiyordu */
        );

        cartCounters.forEach(counter => {
          if (counter) {
            counter.textContent = itemCount;
            if (counter.setAttribute) {
              counter.setAttribute('data-cart-count', itemCount);
            }
          }
        });

        triggerCartEvents(cart);

        log('Cart updated:', itemCount);
      })
      .catch(error => {
        logError('Cart update error:', error.message || error);
      });
  }

  forceMultiplier();

})();